// The mini build of zod, as in contracts.ts: these schemas also run in the browser.
import * as z from "zod/mini";
import { formatOwnerPath, GIT_HOST_KEYS } from "../address.js";
import { REST_ROUTES, restRepositoryCardSchema } from "./contracts.js";
// People (docs/specs/permissions.md): signing in through the git host, what a signed-in person
// has here, the page of an account, and the consent a client asks a person for. Contract:
// docs/specs/rest.md. Absent values are `null` on the wire, never missing keys.
/** Where a browser signs in and out. These are navigations and one form post, not the REST API. */
export const AUTH_ROUTES = {
    /** Sends the browser to the git host to sign in, and afterwards to `return_to`. */
    login: "/auth/gh/login",
    /** Where the git host sends the browser back to. */
    callback: "/auth/gh/callback",
    /** A POST from the pages that ends the session. */
    logout: "/auth/logout",
};
/** The parameter of the login route naming the page to come back to: a path of this origin. */
export const RETURN_TO_PARAM = "return_to";
/**
 * The parameter by which the server asks a page to open the sign-in dialog over itself
 * (ADR-0043): any page takes it, and the page drops it from its URL once it has read it.
 */
export const SIGN_IN_PARAM = "sign_in";
/**
 * Why a sign-in did not complete: the person said no at the git host, the attempt was not
 * finished in time or in the browser that began it, or the host did not confirm it.
 */
export const SIGN_IN_FAILURES = ["denied", "expired", "failed"];
/** What that parameter says: only to open the dialog, or why the last attempt did not complete. */
export const SIGN_IN_REQUESTS = ["open", ...SIGN_IN_FAILURES];
/**
 * How a page learns that people can sign in on this deployment: a meta tag the server writes
 * into the head, naming the git host they sign in through. Without it there is no sign-in.
 */
export const AUTH_META_NAME = "skillcdn-auth";
/** The pages of whoever is signed in. */
export const ACCOUNT_PAGE_PATH = "/account";
/** The page that asks a person whether a client may read an address for them. */
export const CONSENT_PAGE_PATH = "/oauth/consent";
/** What the consent page was sent to ask about, as the authorization endpoint sealed it. */
export const CONSENT_REQUEST_PARAM = "request";
/** Why there is nothing to ask: a request the authorization endpoint could not accept. */
export const CONSENT_ERROR_PARAM = "error";
/**
 * Where a browser leaves for the git host to sign in and come back to `returnTo`, a path of this
 * origin.
 */
export function loginPath(returnTo) {
    return `${AUTH_ROUTES.login}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`;
}
/**
 * A page of this origin, a path with or without a query, asked to open the sign-in dialog over
 * itself: the page a sign-in was for is where it is offered, and offered again.
 */
export function signInPath(page, request = "open") {
    return `${page}${page.includes("?") ? "&" : "?"}${SIGN_IN_PARAM}=${request}`;
}
/** The REST path of the page of an account. */
export function ownerRestPath(owner) {
    return `${REST_ROUTES.owners}${formatOwnerPath(owner)}`;
}
const count = z.int().check(z.nonnegative());
const accountKind = z.enum(["organization", "user"]);
/** A person who signed in, as the pages show them. Nothing here is a secret. */
export const restUserSchema = z.object({
    host: z.enum(GIT_HOST_KEYS),
    login: z.string(),
    /** The name the account goes by at the host, or `null`. */
    name: z.nullable(z.string()),
    /** The account's picture as the host serves it: an https URL, loaded by the browser. */
    avatar: z.string(),
});
/** `GET /api/v1/me`: who is signed in, or nobody. */
export const restMeSchema = z.object({ user: z.nullable(restUserSchema) });
/**
 * `GET /api/v1/me/repositories`: the repositories the person can reach through the git host's
 * app, by where it is installed, and where to install it on more.
 */
export const restMyRepositoriesSchema = z.object({
    /** Where the app is added to an account or to repositories, at the host; `null` when unknown. */
    installUrl: z.nullable(z.string()),
    installations: z.array(z.object({
        account: z.object({ login: z.string(), kind: accountKind, avatar: z.string() }),
        /** Where the person changes which repositories the app may read, at the host. */
        manageUrl: z.nullable(z.string()),
        selection: z.enum(["all", "selected"]),
        repositories: z.array(z.object({
            address: z.string(),
            /** As the host spells them, for display. */
            owner: z.string(),
            name: z.string(),
            description: z.nullable(z.string()),
            visibility: z.enum(["public", "private"]),
        })),
        /** True when the account has more repositories than are listed. */
        truncated: z.boolean(),
    })),
    /** True when the person can reach more installations than are listed. */
    truncated: z.boolean(),
});
/**
 * `GET /api/v1/me/index/<address>`: what the person may ask of the repository's index (ADR-0064):
 * whether they administer the repository at the git host, which is what asking takes.
 */
export const restMyIndexSchema = z.object({ administers: z.boolean() });
/** `POST /api/v1/me/index/<address>`: the repository was queued to be indexed now, or waits in the queue already. */
export const restIndexNowSchema = z.object({ queued: z.boolean() });
/** `GET /api/v1/me/grants`: the clients the person let read an address for them. */
export const restGrantsSchema = z.object({
    items: z.array(z.object({
        id: z.string(),
        client: z.object({
            name: z.string(),
            /** The client's own page, when it names one; shown as text, never followed for it. */
            uri: z.nullable(z.string()),
        }),
        /** The address the client may read: canonical, as the grammar prints it. */
        address: z.string(),
        /** ISO 8601 instants. */
        createdAt: z.string(),
        lastUsedAt: z.nullable(z.string()),
    })),
});
/** What a repository token begins with, so that whoever finds one knows what it found. */
export const REPO_TOKEN_PREFIX = "scdn_repo_";
/**
 * The longest lifetime a repository token may be given, in days, and the lifetimes the pages
 * offer. A token may also be made without an end (`expiresInDays: null`, ADR-0060): it then
 * lasts until its maker removes it or the git host stops vouching for them.
 */
export const REPO_TOKEN_MAX_DAYS = 366;
export const REPO_TOKEN_LIFETIMES_DAYS = [7, 30, 90, 365];
export const REPO_TOKEN_DEFAULT_DAYS = 90;
/** How long the name a person gives a token may be. */
export const REPO_TOKEN_MAX_LABEL_LENGTH = 60;
/** A repository token as its maker sees it afterwards: everything but the secret. */
const restRepoTokenSchema = z.object({
    id: z.string(),
    /** The repository it reads: canonical, an address without a ref or a path. */
    address: z.string(),
    /** What its maker called it. */
    label: z.string(),
    /** ISO 8601 instants. */
    createdAt: z.string(),
    /** `null` for a token without an end. */
    expiresAt: z.nullable(z.string()),
    lastUsedAt: z.nullable(z.string()),
});
/**
 * `GET /api/v1/me/tokens`: the tokens the person made for agents that have nobody to sign in,
 * newest first, and how many they may hold at once.
 */
export const restRepoTokensSchema = z.object({
    items: z.array(restRepoTokenSchema),
    limit: count,
});
/** What `POST /api/v1/me/tokens` is sent: the repository, a name for the token, and its lifetime. */
export const restRepoTokenRequestSchema = z.object({
    /** An address without a ref or a path: `/gh/owner/repo`. */
    address: z.string(),
    label: z.string(),
    /** From a day to a year, or `null` for a token without an end. */
    expiresInDays: z.nullable(z.int()),
});
/** The answer to `POST /api/v1/me/tokens`: the secret, this once, and what was stored about it. */
export const restNewRepoTokenSchema = z.object({
    token: z.string(),
    item: restRepoTokenSchema,
});
/**
 * `GET /api/v1/owners/gh/<owner>`: an account of the git host as everyone sees it, the public
 * repositories of it that this deployment has indexed and found skills in, and a page of its
 * other public repositories as the host lists them. Listing indexes nothing.
 */
export const restOwnerSchema = z.object({
    owner: z.object({
        host: z.enum(GIT_HOST_KEYS),
        /** As the host spells it, for display. */
        login: z.string(),
        name: z.nullable(z.string()),
        kind: accountKind,
        avatar: z.string(),
        bio: z.nullable(z.string()),
        /** The account's page at the host. */
        url: z.string(),
        publicRepositories: count,
    }),
    /** On the first page only: what is already indexed and has skills, most skills first. */
    indexed: z.array(restRepositoryCardSchema),
    /** The rest, most recently pushed first, without what `indexed` already shows. */
    repositories: z.array(z.object({
        address: z.string(),
        name: z.string(),
        description: z.nullable(z.string()),
        fork: z.boolean(),
        archived: z.boolean(),
        stars: count,
        /** An ISO 8601 instant, or `null` when the host does not say. */
        pushedAt: z.nullable(z.string()),
    })),
    /** The page to ask for next, or `null` when this is the last. */
    nextPage: z.nullable(z.int()),
});
/**
 * `GET /api/v1/oauth/request`: what a client asked to be authorized for, for the consent page to
 * show before anyone agrees to it.
 */
export const restAuthorizationSchema = z.object({
    client: z.object({
        /** What the client calls itself. It is the client's word: shown as text, vouched for by nobody. */
        name: z.string(),
        uri: z.nullable(z.string()),
        /**
         * Who the person is sent back to with the answer, which is what actually receives the
         * access: the host of a web address, or the scheme of an app's own (`cursor:`). Never a
         * host that an app's own scheme merely writes after itself.
         */
        redirectHost: z.string(),
        /**
         * True when that is an app on the person's own computer rather than a site: one of the
         * computer's own addresses, or a link scheme an installed app answers to.
         */
        loopback: z.boolean(),
    }),
    /** The address the client asks to read: canonical, as the grammar prints it. */
    address: z.string(),
    scope: z.array(z.string()),
    /** Who is signed in and would be agreeing; `null` when nobody is. */
    user: z.nullable(restUserSchema),
    /**
     * Whether that person can see the repository right now; `null` while nobody is signed in. A
     * repository that does not exist and one they may not see are the same `false`.
     */
    visible: z.nullable(z.boolean()),
    /** Where the app is added to repositories at the host, for when `visible` is false. */
    installUrl: z.nullable(z.string()),
});
/** The answer to `POST /api/v1/oauth/decision`: where the browser goes with the person's answer. */
export const restAuthorizationDecisionSchema = z.object({ redirect: z.string() });
// A command connects with a code a person approves (docs/specs/permissions.md, ADR-0059): the
// command asks as nobody and is given a code for the person and a secret for itself; the person
// approves on the deployment's own pages, signed in; the command claims a repository token with
// its secret, made then and handed over once. Nobody sees the token.
/** The page where a person approves a command: `/connect`, and `/connect/<code>` for one code. */
export const CONNECT_PAGE_PATH = "/connect";
/**
 * The letters a code is spelled from: no `0`, `O`, `1` or `I`, so that a code read aloud, typed
 * or relayed through a chat is the code. Eight of them, shown as two groups of four.
 */
export const CONNECT_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const CONNECT_CODE_LENGTH = 8;
/** How long what a command calls itself may be. */
export const CONNECT_AGENT_MAX_LENGTH = 80;
/** What the secret a command holds while it waits begins with. */
export const CONNECT_SECRET_PREFIX = "scdn_cx_";
/**
 * A code as a person may have typed it, read back as the code: letters of the alphabet in either
 * case, with or without the hyphen or a space between the groups. `undefined` for anything else.
 */
export function parseConnectCode(text) {
    const letters = text.trim().toUpperCase().replace(/[-\s]/g, "");
    if (letters.length !== CONNECT_CODE_LENGTH) {
        return undefined;
    }
    for (const letter of letters) {
        if (!CONNECT_CODE_ALPHABET.includes(letter)) {
            return undefined;
        }
    }
    return `${letters.slice(0, CONNECT_CODE_LENGTH / 2)}-${letters.slice(CONNECT_CODE_LENGTH / 2)}`;
}
/** The page of one code. */
export function connectPagePath(code) {
    return `${CONNECT_PAGE_PATH}/${code}`;
}
/** What `POST /api/v1/connect` is sent: the repository the command wants to read, and what it calls itself. */
export const restConnectRequestSchema = z.object({
    /** An address without a ref or a path: `/gh/owner/repo`. */
    address: z.string(),
    /** What the command calls itself and where it runs, for the person to recognize: `Claude Code on a laptop`. */
    agent: z.string(),
});
/** The answer to `POST /api/v1/connect`: what the command shows the person, and what it keeps. */
export const restConnectStartedSchema = z.object({
    /** For the person: `ABCD-EFGH`. */
    code: z.string(),
    /** The page of the code at this deployment, for the person to open signed in. */
    url: z.string(),
    /** For the command alone: what it claims the token with. Shown to nobody. */
    secret: z.string(),
    /** An ISO 8601 instant: until when the person may approve. */
    expiresAt: z.string(),
    /** How many seconds the command waits between claims. */
    intervalSeconds: z.int(),
});
/** `GET /api/v1/connect/<code>`: what asks to connect, for the person who is signed in. */
export const restConnectViewSchema = z.object({
    code: z.string(),
    /** What the command calls itself. It is the command's word: shown as text, vouched for by nobody. */
    agent: z.string(),
    /** The repository it asks to read: canonical, an address without a ref or a path. */
    address: z.string(),
    expiresAt: z.string(),
    /** True once a person approved and the command has not claimed its token yet. */
    approved: z.boolean(),
    /** Who is signed in and would be approving. */
    user: restUserSchema,
    /**
     * Whether that person can see the repository right now, `null` when the git host could not be
     * asked. A repository that does not exist and one they may not see are the same `false`.
     */
    visible: z.nullable(z.boolean()),
    /** Where the app is added to repositories at the host, for when `visible` is false. */
    installUrl: z.nullable(z.string()),
});
/**
 * What `POST /api/v1/connect/<code>/decision` is sent: yes, with what to call the token and how
 * long it is good for, or no.
 */
export const restConnectDecisionSchema = z.object({
    approve: z.boolean(),
    /** The name the token is listed under; what the command calls itself when left out. */
    label: z.optional(z.string()),
    /** From a day to a year, or `null` for no end; the pages' default when left out. */
    expiresInDays: z.optional(z.nullable(z.int())),
});
export const restConnectDecidedSchema = z.object({ status: z.enum(["approved", "denied"]) });
/** What `POST /api/v1/connect/claim` is sent: the secret the command was given. */
export const restConnectClaimSchema = z.object({ secret: z.string() });
/**
 * The answer to a claim: wait, until the person approved; or the token, this once, with what the
 * person's token list says about it.
 */
export const restConnectClaimedSchema = z.union([
    z.object({ status: z.literal("pending"), expiresAt: z.string() }),
    z.object({ status: z.literal("connected"), token: z.string(), item: restRepoTokenSchema }),
]);
//# sourceMappingURL=account.js.map